'use client';

/**
 * Permission Provider
 *
 * Centralized permission context for all UI components.
 * Provides permission checking, tier validation, and lock state computation.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import type { LockConfig, LockState, PermissionContextValue, UserLicenseInfo } from '@ops-matrix/shared-types';
import { SubscriptionTier } from '@ops-matrix/shared-types';
import React, { createContext, useCallback, useContext, useMemo } from 'react';

// ============================================================================
// Tier Hierarchy
// ============================================================================

/**
 * Tier hierarchy for comparison
 * Higher index = higher tier
 */
const TIER_HIERARCHY: SubscriptionTier[] = [
  SubscriptionTier.FREE,
  SubscriptionTier.STARTER,
  SubscriptionTier.PROFESSIONAL,
  SubscriptionTier.ENTERPRISE,
];

/**
 * Get tier index for comparison
 */
function getTierIndex(tier: SubscriptionTier): number {
  const index = TIER_HIERARCHY.indexOf(tier);
  return index >= 0 ? index : 0;
}

/**
 * Human-readable tier names
 */
const TIER_LABELS: Record<SubscriptionTier, string> = {
  [SubscriptionTier.FREE]: 'Free',
  [SubscriptionTier.STARTER]: 'Starter',
  [SubscriptionTier.PROFESSIONAL]: 'Professional',
  [SubscriptionTier.ENTERPRISE]: 'Enterprise',
};

// ============================================================================
// Admin Roles (for permission bypass)
// ============================================================================

/**
 * Roles that have implicit access to all permissions.
 *
 * SECURITY NOTE: This is a UI-only bypass for display purposes.
 * The actual permission enforcement MUST happen on the backend via:
 * - JWT token validation
 * - @CheckPolicies decorator
 * - CaslAbilityFactory
 *
 * The frontend permission check is for:
 * - Showing/hiding UI elements
 * - Displaying lock states
 * - UX purposes only
 *
 * @see CLAUDE.md Section 2c - Enterprise Permission System
 */
const ADMIN_ROLES = new Set(['super_admin', 'platform_admin', 'business_admin']);

/**
 * Check if a role has admin bypass (all permissions).
 */
export function isAdminRole(role: string): boolean {
  return ADMIN_ROLES.has(role);
}

// ============================================================================
// Context
// ============================================================================

const PermissionContext = createContext<PermissionContextValue | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

export interface PermissionProviderProps {
  children: React.ReactNode;

  /** User's permission strings (e.g., ['documents:read', 'cases:create']) */
  permissions?: string[];

  /** User's role */
  role?: string;

  /** Current subscription tier */
  tier?: SubscriptionTier;

  /** User's licenses */
  userLicenses?: UserLicenseInfo[];

  /** Business-level licenses */
  businessLicenses?: UserLicenseInfo[];

  /** Whether permissions are still loading */
  isLoading?: boolean;

  /** Custom lock click handler for tier locks */
  onTierLockClick?: (requiredTier: SubscriptionTier) => void;

  /** Custom lock click handler for permission locks */
  onPermissionLockClick?: (requiredPermission: string) => void;

  /** Custom lock click handler for license locks */
  onLicenseLockClick?: (requiredLicense: string) => void;
}

// ============================================================================
// Provider Component
// ============================================================================

/**
 * Permission Provider
 *
 * Wraps the application to provide centralized permission checking.
 *
 * @example
 * ```tsx
 * <PermissionProvider
 *   permissions={session?.permissions ?? []}
 *   role={session?.role ?? 'business_viewer'}
 *   tier={session?.effectiveBilling?.subscriptionTier ?? SubscriptionTier.FREE}
 *   userLicenses={session?.licenses ?? []}
 * >
 *   <App />
 * </PermissionProvider>
 * ```
 */
export function PermissionProvider({
  children,
  permissions = [],
  role = 'business_viewer',
  tier = SubscriptionTier.FREE,
  userLicenses = [],
  businessLicenses = [],
  isLoading = false,
  onTierLockClick,
  onPermissionLockClick,
  onLicenseLockClick,
}: PermissionProviderProps) {
  // Create permission set for O(1) lookup
  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  // Combine user and business licenses
  const allLicenses = useMemo(() => [...userLicenses, ...businessLicenses], [userLicenses, businessLicenses]);

  // Create active license set for O(1) lookup
  const activeLicenseTypes = useMemo(() => {
    const now = new Date();
    return new Set(
      allLicenses
        .filter((license) => {
          if (license.status !== 'active') return false;
          if (license.expiresAt) {
            const expiresAt = typeof license.expiresAt === 'string' ? new Date(license.expiresAt) : license.expiresAt;
            if (expiresAt < now) return false;
          }
          return true;
        })
        .map((license) => license.type),
    );
  }, [allLicenses]);

  // ============================================================================
  // Permission Check Functions
  // ============================================================================

  const can = useCallback(
    (permission: string): boolean => {
      // Admin roles bypass all permission checks (UI-only, backend enforces actual security)
      if (isAdminRole(role)) return true;
      // Wildcard permission grants all access (Issue #431)
      if (permissionSet.has('*')) return true;
      return permissionSet.has(permission);
    },
    [permissionSet, role],
  );

  const canAny = useCallback(
    (perms: string[]): boolean => {
      // Admin roles bypass all permission checks (UI-only, backend enforces actual security)
      if (isAdminRole(role)) return true;
      // Wildcard permission grants all access (Issue #431)
      if (permissionSet.has('*')) return true;
      return perms.some((p) => permissionSet.has(p));
    },
    [permissionSet, role],
  );

  const canAll = useCallback(
    (perms: string[]): boolean => {
      // Admin roles bypass all permission checks (UI-only, backend enforces actual security)
      if (isAdminRole(role)) return true;
      // Wildcard permission grants all access (Issue #431)
      if (permissionSet.has('*')) return true;
      return perms.every((p) => permissionSet.has(p));
    },
    [permissionSet, role],
  );

  const hasTier = useCallback(
    (requiredTier: SubscriptionTier): boolean => {
      return getTierIndex(tier) >= getTierIndex(requiredTier);
    },
    [tier],
  );

  const hasLicense = useCallback(
    (licenseType: string): boolean => {
      return activeLicenseTypes.has(licenseType);
    },
    [activeLicenseTypes],
  );

  const hasAnyLicense = useCallback(
    (licenseTypes: string[]): boolean => {
      return licenseTypes.some((lt) => activeLicenseTypes.has(lt));
    },
    [activeLicenseTypes],
  );

  // ============================================================================
  // Lock State Computation
  // ============================================================================

  const getLockState = useCallback(
    (config: LockConfig): LockState | null => {
      // Admin roles bypass all locks (UI-only, backend enforces actual security)
      if (isAdminRole(role)) {
        return null;
      }

      // Check tier first (most common upgrade path)
      if (config.requiredTier && !hasTier(config.requiredTier)) {
        return {
          type: 'tier',
          reason: `Requires ${TIER_LABELS[config.requiredTier]} plan or higher`,
          requiredTier: config.requiredTier,
          onLockedClick: onTierLockClick ? () => onTierLockClick(config.requiredTier!) : undefined,
        };
      }

      // Check license requirements
      if (config.requiredLicense && !hasLicense(config.requiredLicense)) {
        return {
          type: 'license',
          reason: `Requires ${config.requiredLicense} license`,
          requiredLicense: config.requiredLicense,
          onLockedClick: onLicenseLockClick ? () => onLicenseLockClick(config.requiredLicense!) : undefined,
        };
      }

      if (config.anyLicense && !hasAnyLicense(config.anyLicense)) {
        const licenseList = config.anyLicense.join(' or ');
        return {
          type: 'license',
          reason: `Requires one of: ${licenseList}`,
          requiredLicense: config.anyLicense[0],
          onLockedClick: onLicenseLockClick ? () => onLicenseLockClick(config.anyLicense![0]) : undefined,
        };
      }

      // Check permission requirements
      if (config.permission && !can(config.permission)) {
        return {
          type: 'permission',
          reason: 'You do not have permission to access this feature',
          requiredPermission: config.permission,
          onLockedClick: onPermissionLockClick ? () => onPermissionLockClick(config.permission!) : undefined,
        };
      }

      if (config.anyPermission && !canAny(config.anyPermission)) {
        return {
          type: 'permission',
          reason: 'You do not have permission to access this feature',
          requiredPermission: config.anyPermission[0],
          onLockedClick: onPermissionLockClick ? () => onPermissionLockClick(config.anyPermission![0]) : undefined,
        };
      }

      if (config.allPermissions && !canAll(config.allPermissions)) {
        const missingPermission = config.allPermissions.find((p) => !can(p)) ?? config.allPermissions[0];
        return {
          type: 'permission',
          reason: 'You do not have all required permissions',
          requiredPermission: missingPermission,
          onLockedClick: onPermissionLockClick ? () => onPermissionLockClick(missingPermission) : undefined,
        };
      }

      // Not locked
      return null;
    },
    [
      role,
      can,
      canAny,
      canAll,
      hasTier,
      hasLicense,
      hasAnyLicense,
      onTierLockClick,
      onPermissionLockClick,
      onLicenseLockClick,
    ],
  );

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = useMemo<PermissionContextValue>(
    () => ({
      permissions,
      role,
      tier,
      userLicenses,
      businessLicenses,
      can,
      canAny,
      canAll,
      hasTier,
      hasLicense,
      hasAnyLicense,
      getLockState,
      isLoading,
    }),
    [
      permissions,
      role,
      tier,
      userLicenses,
      businessLicenses,
      can,
      canAny,
      canAll,
      hasTier,
      hasLicense,
      hasAnyLicense,
      getLockState,
      isLoading,
    ],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access permission context
 *
 * @throws Error if used outside of PermissionProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { can, hasTier, getLockState } = usePermission();
 *
 *   const lockState = getLockState({
 *     permission: 'documents:delete',
 *     requiredTier: SubscriptionTier.PROFESSIONAL,
 *   });
 *
 *   if (lockState) {
 *     return <LockedFeature reason={lockState.reason} />;
 *   }
 *
 *   return <DeleteButton />;
 * }
 * ```
 */
export function usePermission(): PermissionContextValue {
  const context = useContext(PermissionContext);

  if (!context) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }

  return context;
}

/**
 * Hook to access permission context (optional - returns null if not in provider)
 * Use this when the component may be rendered outside of PermissionProvider
 */
export function usePermissionOptional(): PermissionContextValue | null {
  return useContext(PermissionContext);
}

// ============================================================================
// Utility Functions (for use outside React components)
// ============================================================================

/**
 * Check if a tier meets the required tier level
 */
export function checkTierAccess(currentTier: SubscriptionTier, requiredTier: SubscriptionTier): boolean {
  return getTierIndex(currentTier) >= getTierIndex(requiredTier);
}

/**
 * Get human-readable tier label
 */
export function getTierLabel(tier: SubscriptionTier): string {
  return TIER_LABELS[tier] ?? tier;
}

/**
 * Get all tiers at or above the current tier
 */
export function getAvailableTiers(currentTier: SubscriptionTier): SubscriptionTier[] {
  const currentIndex = getTierIndex(currentTier);
  return TIER_HIERARCHY.slice(currentIndex);
}

/**
 * Get the next tier upgrade option
 */
export function getNextTier(currentTier: SubscriptionTier): SubscriptionTier | null {
  const currentIndex = getTierIndex(currentTier);
  if (currentIndex >= TIER_HIERARCHY.length - 1) return null;
  return TIER_HIERARCHY[currentIndex + 1];
}

// Export context for advanced use cases
export { PermissionContext };
